import React, { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fab,
  InputAdornment,
  Alert,
  Stack,
  Divider,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Receipt as InvoiceIcon,
  LocalShipping as DeliveryIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Inventory as InventoryIcon,
  Assignment as DraftIcon,
  HourglassEmpty as ProcessingIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import InvoiceConversionDialog from '../../components/documents/InvoiceConversionDialog'
import { salesOrderService } from '../../services/salesOrderService'
import { SalesOrder } from '../../types/salesOrder'
import { formatCurrency } from '../../utils/formatters'
import { useAuthStore } from '../../stores/authStore'

const SalesOrdersPage: React.FC = () => {
  const { hasPermission } = useAuthStore()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null)
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [orderToCancel, setOrderToCancel] = useState<SalesOrder | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(12)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const handleCopyPhone = (phone: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!phone) return;
    navigator.clipboard.writeText(phone);
    enqueueSnackbar(`Copied phone number ${phone} to clipboard!`, { variant: 'info', autoHideDuration: 2000 });
  };

  // Fetch sales orders from API
  const { data, isLoading, error } = useQuery({
    queryKey: ['salesOrders', page, pageSize, searchTerm],
    queryFn: () => salesOrderService.getSalesOrders({
      page,
      per_page: pageSize,
      search_term: searchTerm || undefined
    })
  })

  const getStatusConfig = (status: string) => {
    const statusMap: Record<string, { color: any, icon: JSX.Element, label: string, hex: string }> = {
      draft: { color: 'default', icon: <DraftIcon fontSize="small" />, label: 'Draft', hex: '#9e9e9e' },
      confirmed: { color: 'info', icon: <CheckCircleIcon fontSize="small" />, label: 'Confirmed', hex: '#0288d1' },
      processing: { color: 'warning', icon: <ProcessingIcon fontSize="small" />, label: 'Processing', hex: '#ed6c02' },
      ready_to_ship: { color: 'info', icon: <InventoryIcon fontSize="small" />, label: 'Ready to Ship', hex: '#0288d1' },
      shipped: { color: 'primary', icon: <DeliveryIcon fontSize="small" />, label: 'Shipped', hex: '#1976d2' },
      delivered: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Delivered', hex: '#2e7d32' },
      invoiced: { color: 'success', icon: <InvoiceIcon fontSize="small" />, label: 'Invoiced', hex: '#2e7d32' },
      cancelled: { color: 'error', icon: <ScheduleIcon fontSize="small" />, label: 'Cancelled', hex: '#d32f2f' },
    }
    return statusMap[status] || { color: 'default', icon: <ScheduleIcon fontSize="small" />, label: status, hex: '#9e9e9e' }
  }

  const getPaymentStatusConfig = (paymentStatus?: string) => {
    const pStatus = (paymentStatus || 'PENDING').toUpperCase()
    if (pStatus === 'PAID' || pStatus === 'COMPLETED') {
      return { color: 'success' as const, label: 'Payment Paid', hex: '#2e7d32', variant: 'filled' as const }
    } else if (pStatus === 'PARTIALLY_PAID' || pStatus === 'PARTIAL') {
      return { color: 'warning' as const, label: 'Partially Paid', hex: '#ed6c02', variant: 'filled' as const }
    } else if (pStatus === 'FAILED') {
      return { color: 'error' as const, label: 'Payment Failed', hex: '#d32f2f', variant: 'filled' as const }
    } else if (pStatus === 'COD') {
      return { color: 'info' as const, label: 'Cash On Delivery', hex: '#0288d1', variant: 'outlined' as const }
    }
    return { color: 'warning' as const, label: 'Payment Pending', hex: '#ed6c02', variant: 'outlined' as const }
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, order: SalesOrder) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedOrder(order)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleOpenCancelDialog = (order: SalesOrder) => {
    setOrderToCancel(order)
    setCancelDialogOpen(true)
    handleMenuClose()
  }

  const handleConfirmCancelOrder = async () => {
    if (!orderToCancel) return
    try {
      setCancelling(true)
      await salesOrderService.cancelSalesOrder(orderToCancel.id)
      enqueueSnackbar(`Sales Order ${orderToCancel.order_number} cancelled successfully`, { variant: 'success' })
      setCancelDialogOpen(false)
      setOrderToCancel(null)
      queryClient.invalidateQueries({ queryKey: ['salesOrders'] })
    } catch (err: any) {
      console.error('Failed to cancel order:', err)
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to cancel sales order', { variant: 'error' })
    } finally {
      setCancelling(false)
    }
  }

  const handleConvertToInvoice = () => {
    handleMenuClose()
    setInvoiceDialogOpen(true)
  }

  const handleDownloadPdf = async () => {
    const order = selectedOrder
    handleMenuClose()
    if (!order) return
    try {
      const blob = await salesOrderService.downloadSalesOrderPDF(order.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${order.order_number}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      enqueueSnackbar('Failed to download sales order PDF', { variant: 'error' })
    }
  }

  const handleCreateDeliveryChallan = () => {
    const order = selectedOrder
    handleMenuClose()
    if (!order) return
    navigate(`/admin/documents/delivery-challans/create?sales_order_id=${order.id}`)
  }

  const handleInvoiceConversionSuccess = (invoiceId: string, invoiceNumber: string) => {
    enqueueSnackbar(`Invoice ${invoiceNumber} created successfully!`, { variant: 'success' })
    setInvoiceDialogOpen(false)
    setSelectedOrder(null)
    navigate(`/admin/documents/invoices/${invoiceId}`)
  }

  const handleCardClick = (orderId: string) => {
    navigate(`/admin/documents/sales-orders/${orderId}`)
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load sales orders. Please try again later.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Sales Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.total || 0} total orders
          </Typography>
        </Box>
        {hasPermission('sales_orders.create') && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/documents/sales-orders/create')}
            sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
          >
            Create Order
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search by order number, customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="card">
                  <CardViewIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Sales Orders Grid/List */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography>Loading sales orders...</Typography>
        </Box>
      ) : viewMode === 'card' ? (
        <>
          <Grid container spacing={2}>
            {data?.sales_orders?.map((order) => {
              const statusConfig = getStatusConfig(order.status)
              const paymentConfig = getPaymentStatusConfig(order.payment_status)
              const isInvoiced = !!order.converted_to_invoice_id

              const customerName = order.customer?.billing_name || order.customer?.name || order.customer?.contact_person || 'N/A'
              const initials = customerName.split(' ').filter(Boolean).slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()

              return (
                <Grid item xs={12} sm={6} md={4} key={order.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                      transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
                      '&:hover': {
                        boxShadow: `0 12px 28px -8px ${statusConfig.hex}55`,
                        transform: 'translateY(-3px)',
                        borderColor: statusConfig.hex,
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: statusConfig.hex,
                      },
                    }}
                    onClick={() => handleCardClick(order.id)}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1, pt: 2.5 }}>
                      {/* Header with Order Number and Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700} letterSpacing={0.2}>
                            {order.order_number}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                            <Chip
                              icon={statusConfig.icon}
                              label={statusConfig.label}
                              size="small"
                              sx={{
                                borderRadius: 4,
                                fontWeight: 600,
                                bgcolor: `${statusConfig.hex}1A`,
                                color: statusConfig.hex,
                                '& .MuiChip-icon': { color: statusConfig.hex },
                              }}
                            />
                            <Chip
                              icon={<PaymentIcon fontSize="small" />}
                              label={paymentConfig.label}
                              size="small"
                              variant={paymentConfig.variant}
                              color={paymentConfig.color}
                              sx={{ borderRadius: 4, fontWeight: 600, fontSize: '0.75rem' }}
                            />
                            {isInvoiced && order.status !== 'invoiced' && (
                              <Chip
                                icon={<InvoiceIcon fontSize="small" />}
                                label="Invoiced"
                                size="small"
                                variant="outlined"
                                color="success"
                                sx={{ borderRadius: 4, fontWeight: 600 }}
                              />
                            )}
                          </Stack>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, order)}
                          sx={{ mt: -0.5, mr: -1 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Customer Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
                        <Avatar
                          sx={{
                            width: 34,
                            height: 34,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            bgcolor: `${statusConfig.hex}1A`,
                            color: statusConfig.hex,
                          }}
                        >
                          {initials || <PersonIcon fontSize="small" />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {customerName}
                          </Typography>
                          {order.customer?.mobile_no || order.customer?.phone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                📞 {order.customer?.mobile_no || order.customer?.phone}
                              </Typography>
                              <Tooltip title="Copy Phone Number">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleCopyPhone(order.customer?.mobile_no || order.customer?.phone || '', e)}
                                  sx={{ p: 0.25, '&:hover': { color: 'primary.main' } }}
                                >
                                  <CopyIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : null}
                          {order.estimate ? (
                            <Typography variant="caption" color="info.main" fontWeight={500} noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DescriptionIcon sx={{ fontSize: 12 }} /> from {order.estimate.estimate_number}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">Direct Order</Typography>
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ mb: 1.5 }} />

                      {/* Dates */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 1.75 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.disabled">
                              Order Date
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={500}>
                            {format(new Date(order.order_date), 'dd MMM yyyy')}
                          </Typography>
                        </Box>
                        {order.expected_delivery_date && (
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              <DeliveryIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.disabled">
                                Delivery
                              </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={500}>
                              {format(new Date(order.expected_delivery_date), 'dd MMM yyyy')}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Amount */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          bgcolor: 'rgba(46, 125, 50, 0.08)',
                          borderRadius: 1.5,
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <MoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Total Amount
                          </Typography>
                        </Box>
                        <Typography variant="subtitle1" fontWeight={700} color="success.dark">
                          {formatCurrency(order.total_amount)}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ px: 2, pb: 1.5, pt: 0.5, gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/admin/documents/sales-orders/${order.id}`)
                          }}
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/admin/documents/sales-orders/${order.id}/edit`)
                            }}
                            disabled={order.status === 'cancelled'}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Download PDF">
                        <IconButton
                          size="small"
                          onClick={async (e) => {
                            e.stopPropagation()
                            try {
                              const blob = await salesOrderService.downloadSalesOrderPDF(order.id)
                              const url = window.URL.createObjectURL(blob)
                              const a = document.createElement('a')
                              a.href = url
                              a.download = `${order.order_number}.pdf`
                              a.click()
                              window.URL.revokeObjectURL(url)
                            } catch {
                              enqueueSnackbar('Failed to download sales order PDF', { variant: 'error' })
                            }
                          }}
                          sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                          <PdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Box sx={{ flex: 1 }} />
                      {order.status !== 'cancelled' && (
                        <Tooltip title="Cancel Order">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenCancelDialog(order)
                            }}
                            sx={{ border: '1px solid', borderColor: 'error.light' }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isInvoiced && (
                        <Tooltip title="Convert to Invoice">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedOrder(order)
                              setInvoiceDialogOpen(true)
                            }}
                            sx={{ border: '1px solid', borderColor: 'success.main', color: 'success.main' }}
                          >
                            <InvoiceIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
          </Grid>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ borderRadius: 2 }}
                >
                  Previous
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                  <Typography variant="body2">
                    Page {page} of {data.total_pages}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  disabled={page === data.total_pages}
                  onClick={() => setPage(page + 1)}
                  sx={{ borderRadius: 2 }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </>
      ) : (
        <>
          {/* List View */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Order Number</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Order Date</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Expected Delivery</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Order Status</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Payment Status</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Total Amount</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Invoice Status</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.sales_orders?.map((order) => {
                  const statusConfig = getStatusConfig(order.status)
                  const paymentConfig = getPaymentStatusConfig(order.payment_status)
                  return (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleCardClick(order.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="primary">
                          {order.order_number}
                        </Typography>
                        {order.estimate && (
                          <Typography variant="caption" color="textSecondary">
                            From: {order.estimate.estimate_number}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {order.customer?.billing_name || order.customer?.name || order.customer?.contact_person || 'N/A'}
                        </Typography>
                        {(order.customer?.mobile_no || order.customer?.phone) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <Typography variant="caption" color="textSecondary">
                              📞 {order.customer?.mobile_no || order.customer?.phone}
                            </Typography>
                            <Tooltip title="Copy Phone Number">
                              <IconButton
                                size="small"
                                onClick={(e) => handleCopyPhone(order.customer?.mobile_no || order.customer?.phone || '', e)}
                                sx={{ p: 0.25, '&:hover': { color: 'primary.main' } }}
                              >
                                <CopyIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(order.order_date), 'dd/MM/yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.expected_delivery_date
                            ? format(new Date(order.expected_delivery_date), 'dd/MM/yyyy')
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusConfig.icon}
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small"
                          sx={{ borderRadius: 4 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<PaymentIcon fontSize="small" />}
                          label={paymentConfig.label}
                          size="small"
                          variant={paymentConfig.variant}
                          color={paymentConfig.color}
                          sx={{ borderRadius: 4, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(order.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {order.converted_to_invoice_id ? (
                          <Chip
                            label="Invoiced"
                            color="success"
                            size="small"
                            icon={<InvoiceIcon />}
                            sx={{ borderRadius: 4 }}
                          />
                        ) : (
                          <Chip
                            label="Not Invoiced"
                            variant="outlined"
                            size="small"
                            sx={{ borderRadius: 4 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/admin/documents/sales-orders/${order.id}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/documents/sales-orders/${order.id}/edit`)}
                              disabled={order.status === 'cancelled'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {order.status !== 'cancelled' && (
                            <Tooltip title="Cancel Order">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenCancelDialog(order)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="More Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, order)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination for List View */}
          {data && data.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={data.total_pages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 200 }
          }
        }}
      >
        <MenuItem onClick={() => {
          navigate(`/admin/documents/sales-orders/${selectedOrder?.id}`)
          handleMenuClose()
        }}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          navigate(`/admin/documents/sales-orders/${selectedOrder?.id}/edit`)
          handleMenuClose()
        }} disabled={selectedOrder?.status === 'cancelled'}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadPdf}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCreateDeliveryChallan}>
          <ListItemIcon><DeliveryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Create Delivery Challan</ListItemText>
        </MenuItem>
        {!selectedOrder?.converted_to_invoice_id && (
          <MenuItem onClick={handleConvertToInvoice}>
            <ListItemIcon><InvoiceIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Convert to Invoice</ListItemText>
          </MenuItem>
        )}
        {selectedOrder?.status !== 'cancelled' && (
          <MenuItem 
            onClick={() => selectedOrder && handleOpenCancelDialog(selectedOrder)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Cancel Order</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Cancel Sales Order
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel Sales Order <strong>{orderToCancel?.order_number}</strong>?
            This will release any reserved inventory back to warehouse stock and mark the order as Cancelled.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={cancelling}
          >
            Go Back
          </Button>
          <Button
            onClick={handleConfirmCancelOrder}
            color="error"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Confirm Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Conversion Dialog */}
      {selectedOrder && (
        <InvoiceConversionDialog
          open={invoiceDialogOpen}
          salesOrderId={selectedOrder.id}
          salesOrderNumber={selectedOrder.order_number}
          onClose={() => {
            setInvoiceDialogOpen(false)
            setSelectedOrder(null)
          }}
          onSuccess={handleInvoiceConversionSuccess}
        />
      )}

      {/* Floating Action Button */}
      {hasPermission('sales_orders.create') && (
        <Fab
          color="secondary"
          aria-label="add"
          onClick={() => navigate('/admin/documents/sales-orders/create')}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  )
}

export default SalesOrdersPage
